import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, orderBy, limit, type DocumentData } from "firebase/firestore";
import { db } from "./firebase";
import { useEffectiveTenantId } from "./impersonation";

export type TenantStats = {
  totalCustomers: number;
  totalAmbassadors: number;
  pendingApplications: number;
  totalPurchases: number;
  totalRevenue: number;
  totalTicketsEmitted: number;
  unredeemedTickets: number;
  totalRewards: number;
  totalCommissions: number;
  loading: boolean;
};

const initial: TenantStats = {
  totalCustomers: 0,
  totalAmbassadors: 0,
  pendingApplications: 0,
  totalPurchases: 0,
  totalRevenue: 0,
  totalTicketsEmitted: 0,
  unredeemedTickets: 0,
  totalRewards: 0,
  totalCommissions: 0,
  loading: true
};

/** Subscribe a stats live del tenant. Para tenants grandes esto se reemplaza con counters denormalizados en M11. */
export function useTenantStats(): TenantStats {
  const tenantId = useEffectiveTenantId();
  const [stats, setStats] = useState<TenantStats>(initial);

  useEffect(() => {
    if (!tenantId) {
      setStats({ ...initial, loading: false });
      return;
    }
    const tid = tenantId;
    const unsubs: Array<() => void> = [];

    const setMerge = (k: keyof TenantStats, v: number) =>
      setStats((s) => ({ ...s, [k]: v, loading: false }));

    const sumField = (docs: DocumentData[], field: string): number =>
      docs.reduce((acc, d) => acc + (typeof d[field] === "number" ? d[field] : 0), 0);

    // Users (role=customer) — usa where simple
    unsubs.push(
      onSnapshot(
        query(collection(db, "tenants", tid, "users"), where("role", "==", "customer")),
        (s) => setMerge("totalCustomers", s.size)
      )
    );

    // Ambassadors activos
    unsubs.push(
      onSnapshot(
        query(collection(db, "tenants", tid, "ambassadors"), where("active", "==", true)),
        (s) => setMerge("totalAmbassadors", s.size)
      )
    );

    // Applications pending
    unsubs.push(
      onSnapshot(
        query(collection(db, "tenants", tid, "ambassadorApplications"), where("status", "==", "pending")),
        (s) => setMerge("pendingApplications", s.size)
      )
    );

    // Purchases approved + revenue
    unsubs.push(
      onSnapshot(
        query(collection(db, "tenants", tid, "purchases"), where("status", "==", "approved")),
        (s) => {
          const data = s.docs.map((d) => d.data());
          setStats((st) => ({
            ...st,
            totalPurchases: s.size,
            totalRevenue: sumField(data, "amount"),
            loading: false
          }));
        }
      )
    );

    // Tickets totales + unredeemed
    unsubs.push(
      onSnapshot(collection(db, "tenants", tid, "tickets"), (s) => {
        const unred = s.docs.filter((d) => d.data().redeemed === false).length;
        setStats((st) => ({
          ...st,
          totalTicketsEmitted: s.size,
          unredeemedTickets: unred,
          loading: false
        }));
      })
    );

    // Rewards
    unsubs.push(
      onSnapshot(collection(db, "tenants", tid, "rewards"), (s) =>
        setMerge("totalRewards", s.size)
      )
    );

    // Commissions
    unsubs.push(
      onSnapshot(collection(db, "tenants", tid, "commissions"), (s) => {
        const data = s.docs.map((d) => d.data());
        setMerge("totalCommissions", sumField(data, "commissionAmount"));
      })
    );

    return () => unsubs.forEach((u) => u());
  }, [tenantId]);

  return stats;
}

export type RecentItem = {
  kind: "purchase" | "application" | "redeem";
  id: string;
  label: string;
  at: Date;
};

export function useTenantRecentActivity(maxItems = 6) {
  const tenantId = useEffectiveTenantId();
  const [items, setItems] = useState<RecentItem[]>([]);

  useEffect(() => {
    if (!tenantId) return;
    const tid = tenantId;
    const unsubs: Array<() => void> = [];
    const buckets: Record<string, RecentItem[]> = { purchase: [], application: [], redeem: [] };

    const recompute = () => {
      const merged = [
        ...buckets.purchase,
        ...buckets.application,
        ...buckets.redeem
      ].sort((a, b) => b.at.getTime() - a.at.getTime()).slice(0, maxItems);
      setItems(merged);
    };

    unsubs.push(
      onSnapshot(
        query(
          collection(db, "tenants", tid, "purchases"),
          orderBy("createdAt", "desc"),
          limit(maxItems)
        ),
        (s) => {
          buckets.purchase = s.docs.map((d) => {
            const x = d.data();
            const amount = typeof x.amount === "number" ? x.amount : 0;
            return {
              kind: "purchase" as const,
              id: d.id,
              label: `Compra $${amount.toLocaleString("es-CL")} (${x.status})`,
              at: (x.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date()
            };
          });
          recompute();
        }
      )
    );

    unsubs.push(
      onSnapshot(
        query(
          collection(db, "tenants", tid, "ambassadorApplications"),
          orderBy("createdAt", "desc"),
          limit(maxItems)
        ),
        (s) => {
          buckets.application = s.docs.map((d) => {
            const x = d.data();
            return {
              kind: "application" as const,
              id: d.id,
              label: `Postulación: ${x.name ?? "—"} (${x.status})`,
              at: (x.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date()
            };
          });
          recompute();
        }
      )
    );

    unsubs.push(
      onSnapshot(
        query(
          collection(db, "tenants", tid, "rewards"),
          orderBy("redeemedAt", "desc"),
          limit(maxItems)
        ),
        (s) => {
          buckets.redeem = s.docs.map((d) => {
            const x = d.data();
            return {
              kind: "redeem" as const,
              id: d.id,
              label: `Reward canjeado (${x.ticketsUsed} tickets)`,
              at: (x.redeemedAt as { toDate?: () => Date })?.toDate?.() ?? new Date()
            };
          });
          recompute();
        }
      )
    );

    return () => unsubs.forEach((u) => u());
  }, [tenantId, maxItems]);

  return items;
}
